import {useEffect, useRef, useState} from "react";
import {
    AVATAR_ELEMENT_TYPES, AVATAR_OFFSET_LIMIT, AVATAR_SCALE_MAX, AVATAR_SCALE_MIN, AVATAR_TYPE_LABELS,
    AvatarCatalogue, AvatarElementType, AvatarPicks, elementLabel, layerBox, OPTIONAL_AVATAR_TYPES, resolveElement,
} from "../../models/avatars.model";
import AvatarLayers, {drawnElement, elementImageUrl, TransientFit} from "./avatarLayers.element";

// The stage around the canvas, as a multiple of the canvas: what spills past the
// canvas edge stays visible (dimmed — in game it is clipped by the frame).
const VIEWPORT_RATIO = 1.5;
// Each wheel notch scales by this factor; the notches of one gesture commit together.
const WHEEL_FACTOR = 1.05;
const WHEEL_COMMIT_MS = 350;
// Sizes the client shows avatars at, smallest last.
const PREVIEW_SIZES = [128, 64, 40];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// The Editor's buttons have no disabled look of their own, so a slot chip that
// cannot be clicked is dimmed here.
const chipStyle = (disabled: boolean): React.CSSProperties =>
    disabled ? {opacity: 0.4, cursor: 'not-allowed', filter: 'none'} : {};

// The Avatar tab's main area: the test avatar large on a canvas with a centre cross,
// the selected element outlined. Dragging moves the selected element, the wheel
// scales it; both preview live through onPreview and land in the draft once, through
// onCommit. Above: the test avatar's pick per slot; below: the same avatar at the
// sizes the game shows it.
function AvatarStage(props: {
    catalogue: AvatarCatalogue;
    picks: AvatarPicks;
    selected?: {type: AvatarElementType; id: number};
    transient?: TransientFit;
    onPreview(fit: TransientFit | undefined): void;
    onCommit(fit: TransientFit): void;
    onTogglePick(type: AvatarElementType): void;
    onSelectPick(type: AvatarElementType): void;
    onRandomize(): void;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);
    const [space, setSpace] = useState({w: 400, h: 400});
    const drag = useRef<{x: number; y: number; start: TransientFit} | undefined>(undefined);
    const wheelTimer = useRef<number | undefined>(undefined);

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;
        const observer = new ResizeObserver(entries => {
            const rect = entries[0].contentRect;
            setSpace({w: rect.width, h: rect.height});
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const canvas = Math.max(120, Math.floor(Math.min(space.w, space.h) / VIEWPORT_RATIO));
    const viewport = Math.floor(canvas * VIEWPORT_RATIO);
    const inset = (viewport - canvas) / 2;

    // The selected element as it currently stands (with any in-flight fit), only when
    // the test avatar actually draws it.
    const selectedElement = props.selected
        ? drawnElement(props.catalogue, props.selected.type, props.picks, props.transient)
        : undefined;
    const editable = selectedElement && props.selected && selectedElement.id === props.selected.id
        ? selectedElement : undefined;
    const editableFit = (): TransientFit | undefined => editable && props.selected
        ? {type: props.selected.type, id: editable.id, scale: editable.scale, offsetX: editable.offsetX, offsetY: editable.offsetY}
        : undefined;

    // Latest values for the native wheel listener, which is bound once.
    const wheelState = useRef({fit: editableFit(), onPreview: props.onPreview, onCommit: props.onCommit});
    wheelState.current = {fit: editableFit(), onPreview: props.onPreview, onCommit: props.onCommit};

    useEffect(() => {
        const node = viewportRef.current;
        if (!node) return;
        // Native and non-passive: React's onWheel cannot preventDefault, and the page
        // would scroll under the gesture.
        function onWheel(e: WheelEvent) {
            const {fit, onPreview, onCommit} = wheelState.current;
            if (!fit) return;
            e.preventDefault();
            const factor = e.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR;
            const next = {...fit, scale: clamp(fit.scale * factor, AVATAR_SCALE_MIN, AVATAR_SCALE_MAX)};
            onPreview(next);
            window.clearTimeout(wheelTimer.current);
            wheelTimer.current = window.setTimeout(() => onCommit(next), WHEEL_COMMIT_MS);
        }
        node.addEventListener('wheel', onWheel, {passive: false});
        return () => node.removeEventListener('wheel', onWheel);
    }, []);

    function onPointerDown(e: React.PointerEvent) {
        const fit = editableFit();
        if (!fit || e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = {x: e.clientX, y: e.clientY, start: fit};
    }

    function draggedFit(e: React.PointerEvent): TransientFit | undefined {
        const current = drag.current;
        if (!current) return undefined;
        const limit = AVATAR_OFFSET_LIMIT;
        return {
            ...current.start,
            offsetX: clamp(current.start.offsetX + (e.clientX - current.x) / canvas, -limit, limit),
            // Screen y grows downwards; the fit's y grows upwards.
            offsetY: clamp(current.start.offsetY - (e.clientY - current.y) / canvas, -limit, limit),
        };
    }

    function onPointerMove(e: React.PointerEvent) {
        const fit = draggedFit(e);
        if (fit) props.onPreview(fit);
    }

    function onPointerUp(e: React.PointerEvent) {
        const fit = draggedFit(e);
        const start = drag.current?.start;
        drag.current = undefined;
        if (!fit || !start) return;
        // A click without a move changes nothing and records no undo step.
        if (start.offsetX === fit.offsetX && start.offsetY === fit.offsetY) props.onPreview(undefined);
        else props.onCommit(fit);
    }

    const outline = editable ? layerBox(editable, canvas) : undefined;

    return (
        <div className={'layout vertical'} style={{flexGrow: 1, minHeight: 0, gap: 12}}>
            {/* The test avatar: one button per slot showing its pick, which selects that
                element. Disabled (dimmed) while the slot has no elements, or for an
                optional slot that is switched off; the +/× beside an optional slot
                switches it on and off. Clicking a tile in the sidebar also sets its slot. */}
            <div className={'layout horizontal center'} style={{gap: 8, flexWrap: 'wrap', flexShrink: 0}}>
                {AVATAR_ELEMENT_TYPES.map(type => {
                    const list = props.catalogue.elements[type];
                    const id = props.picks[type];
                    const element = id === undefined ? undefined : resolveElement(list, id);
                    const optional = OPTIONAL_AVATAR_TYPES.includes(type);
                    const label = AVATAR_TYPE_LABELS[type];
                    const empty = list.length === 0;
                    const isSelectedType = props.selected?.type === type;
                    const value = element ? elementLabel(element) : empty ? 'No elements' : 'Off';
                    return (
                        <div key={type} className={'layout horizontal'} style={{gap: 2}}>
                            <button disabled={!element}
                                    title={element ? `Select this ${label.toLowerCase()}`
                                        : empty ? `No ${label.toLowerCase()} elements yet: add some in the sidebar`
                                        : `Switch the ${label.toLowerCase()} on with + to select it`}
                                    onClick={() => props.onSelectPick(type)}
                                    className={'layout horizontal center'}
                                    style={{
                                        ...chipStyle(!element), gap: 8, padding: '4px 10px 4px 4px',
                                        outline: isSelectedType && element ? '1px solid var(--tint-color)' : undefined,
                                        borderTopRightRadius: optional ? 0 : undefined,
                                        borderBottomRightRadius: optional ? 0 : undefined,
                                    }}>
                                <div className={'image-cell'} style={{width: 32, height: 32, borderWidth: 1, position: 'relative'}}>
                                    {element &&
                                        <img src={elementImageUrl(element)} alt={''} draggable={false}
                                             style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain'}}/>}
                                </div>
                                <div className={'layout vertical'} style={{alignItems: 'flex-start', lineHeight: 1.2}}>
                                    <span className={'muted-text'} style={{fontSize: 11}}>{label}</span>
                                    <span>{value}</span>
                                </div>
                            </button>
                            {optional &&
                                <button disabled={empty}
                                        title={empty ? `No ${label.toLowerCase()} elements yet`
                                            : id !== undefined ? `Leave the ${label.toLowerCase()} out` : `Add a ${label.toLowerCase()}`}
                                        onClick={() => props.onTogglePick(type)}
                                        style={{...chipStyle(empty), padding: '0 8px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}>
                                    <span className={`fa-solid ${id !== undefined ? 'fa-xmark' : 'fa-plus'}`}/>
                                </button>}
                        </div>
                    );
                })}
                <button title={'Pick a random offered element for every slot'} onClick={props.onRandomize}
                        style={{alignSelf: 'stretch'}}>
                    <span className={'fa-solid fa-dice'}/> Randomize
                </button>
            </div>

            <div ref={containerRef} className={'layout vertical center'}
                 style={{flexGrow: 1, minHeight: 0, justifyContent: 'center'}}>
                <div ref={viewportRef}
                     onPointerDown={onPointerDown}
                     onPointerMove={onPointerMove}
                     onPointerUp={onPointerUp}
                     onPointerCancel={() => { drag.current = undefined; props.onPreview(undefined); }}
                     title={editable ? 'Drag to move the selected element, scroll to scale it' : undefined}
                     className={'image-cell'}
                     style={{
                         position: 'relative', width: viewport, height: viewport, overflow: 'hidden',
                         cursor: editable ? 'move' : 'default', touchAction: 'none', flexShrink: 0,
                     }}>
                    <AvatarLayers catalogue={props.catalogue} picks={props.picks} size={canvas}
                                  transient={props.transient} overflow
                                  style={{position: 'absolute', left: inset, top: inset}}/>
                    {/* Everything outside the canvas dimmed: in game it is clipped. */}
                    <div style={{
                        position: 'absolute', left: inset, top: inset, width: canvas, height: canvas,
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)', outline: '1px solid rgba(255,255,255,0.35)',
                        pointerEvents: 'none',
                    }}/>
                    <div style={{position: 'absolute', left: viewport / 2, top: inset, width: 1, height: canvas,
                        background: 'rgba(255,255,255,0.2)', pointerEvents: 'none'}}/>
                    <div style={{position: 'absolute', top: viewport / 2, left: inset, height: 1, width: canvas,
                        background: 'rgba(255,255,255,0.2)', pointerEvents: 'none'}}/>
                    {outline &&
                        <div style={{
                            position: 'absolute', left: inset + outline.left, top: inset + outline.top,
                            width: outline.width, height: outline.height,
                            outline: '1px dashed var(--tint-color)', pointerEvents: 'none',
                        }}/>}
                </div>
            </div>

            <div className={'layout horizontal center'} style={{gap: 16, justifyContent: 'center', flexShrink: 0}}>
                {PREVIEW_SIZES.map(size =>
                    <div key={size} className={'layout vertical center'} style={{gap: 4}}>
                        <AvatarLayers catalogue={props.catalogue} picks={props.picks} size={size}
                                      transient={props.transient}
                                      style={{borderRadius: size * 0.18, backgroundColor: '#21262d'}}/>
                        <span className={'muted-text'} style={{fontSize: 11}}>{size} px</span>
                    </div>)}
            </div>
        </div>
    );
}

export default AvatarStage;
